// InvoiceModal.jsx — PURE JSX POS MODAL VERSION

import React, { useEffect, useState } from "react";
import { X } from "react-feather";
import {
  PDFViewer,
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import { apiService } from "../../../../services/apiService.ts";

// -------------------------------------------------------
// PDF STYLES
// -------------------------------------------------------
const pdfStyles = StyleSheet.create({
  page: { padding: 40, fontSize: 10, fontFamily: "Helvetica" },

  headerRow: { flexDirection: "row", justifyContent: "space-between" },
  logo: { width: 80, height: 40 },

  companyTitle: { fontSize: 18, color: "#006699", marginTop: 6 },
  smallGray: { color: "#555", fontSize: 9 },

  table: { marginTop: 12, borderTopWidth: 1, borderColor: "#000" },
  row: { flexDirection: "row", borderBottomWidth: 0.5, borderColor: "#999" },
  cell: { padding: 4 },

  dateCell: { width: 60 },
  descCell: { flex: 1 },
  amtCell: { width: 80, textAlign: "right" },

  summary: { marginTop: 20, alignItems: "flex-end" },
  summaryRow: { flexDirection: "row", justifyContent: "flex-end" },
  sumLabel: { width: 140, textAlign: "right", fontWeight: "bold" },
  sumValue: { width: 80, textAlign: "right" },

  footer: {
    marginTop: 40,
    fontSize: 9,
    borderTopWidth: 0.5,
    borderColor: "#999",
    paddingTop: 10,
    textAlign: "center",
  },
});

// -------------------------------------------------------
// PDF COMPONENT
// -------------------------------------------------------
function InvoicePDF({ data }) {
  return (
    <Document>
      <Page size="A4" style={pdfStyles.page}>
        {/* TOP HEADER */}
        <View style={pdfStyles.headerRow}>
          {data.logoUrl && <Image style={pdfStyles.logo} src={data.logoUrl} />}
          <View>
            <Text style={pdfStyles.companyTitle}>{data.company}</Text>
            {data.vatNo && (
              <Text style={pdfStyles.smallGray}>VAT No: {data.vatNo}</Text>
            )}
          </View>
        </View>

        {/* ADDRESS */}
        <View style={{ marginTop: 16 }}>
          <Text>{data.guestName}</Text>
          <Text>{data.address}</Text>
        </View>

        {/* BOOKING INFO */}
        <View
          style={{
            marginTop: 16,
            padding: 8,
            borderWidth: 0.5,
            borderColor: "#aaa",
          }}
        >
          <View style={pdfStyles.headerRow}>
            <Text>Conf No: {data.bookingInfo.confNo}</Text>
            <Text>CRS No: {data.bookingInfo.crsNo}</Text>
            <Text>Date: {data.bookingInfo.date}</Text>
            <Text>Time: {data.bookingInfo.time}</Text>
          </View>
        </View>

        {/* STAY INFO */}
        <View style={{ marginTop: 10 }}>
          <View style={pdfStyles.headerRow}>
            <Text>Guests: {data.stayInfo.guests}</Text>
            <Text>Arrival: {data.stayInfo.arrival}</Text>
            <Text>Departure: {data.stayInfo.departure}</Text>
            <Text>Room No: {data.stayInfo.roomNo}</Text>
            <Text>Type: {data.stayInfo.roomType}</Text>
            <Text>Cashier: {data.stayInfo.cashier}</Text>
          </View>
        </View>

        {/* TABLE HEADER */}
        <Text style={{ marginTop: 20, fontWeight: "bold" }}>
          INFORMATION INVOICE
        </Text>

        {/* TABLE BODY */}
        <View style={pdfStyles.table}>
          {data.items.map((item, i) => (
            <View key={i} style={pdfStyles.row}>
              <Text style={[pdfStyles.cell, pdfStyles.dateCell]}>
                {item.date}
              </Text>
              <Text style={[pdfStyles.cell, pdfStyles.descCell]}>
                {item.description}
              </Text>
              <Text style={[pdfStyles.cell, pdfStyles.amtCell]}>
                {item.amount.toFixed(2)}
              </Text>
            </View>
          ))}
        </View>

        {/* SUMMARY */}
        <View style={pdfStyles.summary}>
          <View style={pdfStyles.summaryRow}>
            <Text style={pdfStyles.sumLabel}>Balance:</Text>
            <Text style={pdfStyles.sumValue}>
              SCR {data.balance.toFixed(2)}
            </Text>
          </View>

          <View style={pdfStyles.summaryRow}>
            <Text style={pdfStyles.sumLabel}>VAT:</Text>
            <Text style={pdfStyles.sumValue}>SCR {data.vat.toFixed(2)}</Text>
          </View>

          <View style={pdfStyles.summaryRow}>
            <Text style={pdfStyles.sumLabel}>Equivalent EUR:</Text>
            <Text style={pdfStyles.sumValue}>
              {data.eqBalanceEur.toFixed(2)}
            </Text>
          </View>

          <View style={pdfStyles.summaryRow}>
            <Text style={pdfStyles.sumLabel}>Equivalent USD:</Text>
            <Text style={pdfStyles.sumValue}>
              {data.eqBalanceUsd.toFixed(2)}
            </Text>
          </View>
        </View>

        {/* FOOTER */}
        <View style={pdfStyles.footer}>
          <Text>Guest Signature ___________________________</Text>
          <Text>
            {data.addressFooter} • t {data.phone} • e {data.email} •{" "}
            {data.website}
          </Text>
        </View>
      </Page>
    </Document>
  );
}

// -------------------------------------------------------
// MAIN MODAL COMPONENT
// -------------------------------------------------------
export default function InvoiceModal({
  isOpen,
  onClose,
  invoiceData,
  onContinue,
  accountId,
  staffId,
}) {
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  // --------------------------------
  // LOAD PAYMENT TYPES WHEN NEEDED
  // --------------------------------
  useEffect(() => {
    if (!showPaymentModal) return;

    apiService
      .listPaymentTypes()
      .then((res) => {
        if (res.Success) setPaymentTypes(res.Data);
      })
      .catch((err) => console.error(err));
  }, [showPaymentModal]);

  if (!isOpen) return null;

  // --------------------------------
  // CONFIRM PAYMENT
  // --------------------------------
  const handleConfirmPayment = async () => {
    if (!selectedType) return;

    setIsProcessingPayment(true);

    try {
      // Perform checkout
      await apiService.fullCheckout(accountId, staffId, selectedType);

      if (onContinue) onContinue(selectedType);

      setShowPaymentModal(false);
    } catch (err) {
      console.error("Payment processing failed:", err);
    }

    setIsProcessingPayment(false);
  };

  // ======================================================================
  // MAIN INVOICE MODAL
  // ======================================================================
  return (
    <>
      <div
        className="modal fade show d-block pos-modal"
        style={{
          background: "rgba(0,0,0,0.45)",
          zIndex: 30000,
        }}
      >
        <div className="modal-dialog modal-xl modal-dialog-centered">
          <div className="modal-content rounded-4 shadow-lg">

            {/* HEADER */}
            <div className="modal-header border-0 pb-2">
              <h5 className="modal-title fw-semibold">Invoice</h5>
              <button className="btn btn-light" onClick={onClose}>
                <X size={18} />
              </button>
            </div>

            {/* BODY */}
            <div className="modal-body">
              <div
                className="border rounded"
                style={{ height: "650px", overflow: "hidden" }}
              >
                <PDFViewer width="100%" height="100%">
                  <InvoicePDF data={invoiceData} />
                </PDFViewer>
              </div>
            </div>

            {/* FOOTER */}
            <div className="modal-footer border-0 pt-0">
              <button
                className="btn btn-secondary"
                onClick={() => window.print()}
              >
                Print
              </button>

              <button
                className="btn btn-success"
                onClick={() => setShowPaymentModal(true)}
              >
                Continue
              </button>

            </div>
          </div>
        </div>
      </div>

      {/* ================================================================== */}
      {/* PAYMENT TYPE SELECTION MODAL */}
      {/* ================================================================== */}
      {showPaymentModal && (
        <div
          className="modal fade show d-block pos-modal"
          style={{ background: "rgba(0,0,0,0.45)", zIndex: 31000 }}
        >
          <div className="modal-dialog modal-sm modal-dialog-centered">
            <div className="modal-content rounded-4 shadow-lg">

              {/* HEADER */}
              <div className="modal-header border-0 pb-2">
                <h5 className="modal-title fw-semibold">Select Payment Type</h5>
                <button
                  className="btn btn-light"
                  onClick={() => setShowPaymentModal(false)}
                >
                  <X size={18} />
                </button>
              </div>

              {/* BODY */}
              <div className="modal-body">
                <select
                  className="form-select"
                  value={selectedType || ""}
                  onChange={(e) => setSelectedType(Number(e.target.value))}
                >
                  <option value="">Select type…</option>

                  {paymentTypes.map((pt) => (
                    <option
                      key={pt.PaymentTypeID}
                      value={pt.PaymentTypeID}
                    >
                      {pt.Name}
                    </option>
                  ))}
                </select>
              </div>

              {/* FOOTER */}
              <div className="modal-footer border-0 pt-0">
                <button
                  disabled={!selectedType || isProcessingPayment}
                  className="btn btn-success w-100"
                  onClick={handleConfirmPayment}
                >
                  {isProcessingPayment ? "Processing..." : "Confirm Payment"}
                </button>
              </div>

            </div>
          </div>
        </div>
      )}
    </>
  );
}
